import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ApplyCrmRecordChangesRequest,
  CreateCrmRecordWithDetailsRequest,
  MoveCrmRecordMasterRequest,
} from "@/lib/validation/crm";
import { fetchGeneralApiGet, fetchGeneralApiPost, fetchGeneralApiPut } from "../utils";
import { customerOnboardingInstancesQueryKey } from "../customer";
import { crmMastersQueryKey } from "./masters";
import type {
  ApplyCrmRecordChangesResult,
  CreateCrmRecordWithDetailsResult,
  CrmRecordDetails,
  MoveCrmRecordMasterResult,
} from "./types";

export type {
  ApplyCrmRecordChangesResult,
  CreateCrmRecordWithDetailsResult,
  CrmAddressResult,
  CrmContactResult,
  CrmMasterRef,
  CrmRecordDetails,
  CrmRecordDetailsAuthUser,
  CrmRecordResult,
  MoveCrmRecordMasterResult,
} from "./types";

export function getCrmRecordDetails(recordId: string) {
  return fetchGeneralApiGet<CrmRecordDetails>(`crm/records/${encodeURIComponent(recordId)}/details`);
}

/** One record with its mobile logins' permissions. Under the masters key so CRM writes refresh it. */
export function useGetCrmRecordDetails(recordId: string) {
  return useQuery({
    queryKey: [...crmMastersQueryKey, "record-details", recordId],
    queryFn: async (): Promise<CrmRecordDetails> => {
      const response = await getCrmRecordDetails(recordId);
      if (!response.success) {
        throw new Error(response.message || "Unable to load CRM record");
      }
      return response.data;
    },
    enabled: recordId !== "",
    refetchOnWindowFocus: false,
  });
}

/** Developer only: moves a top-level record, with everything under it, to another master. */
export function moveCrmRecordMaster(request: MoveCrmRecordMasterRequest) {
  return fetchGeneralApiPost<MoveCrmRecordMasterResult>("crm/developer/records/move-master", request);
}

export function useMoveCrmRecordMaster() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: moveCrmRecordMaster,
    // Refresh on every outcome: a revision conflict also needs the latest revision loaded.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: crmMastersQueryKey });
    },
  });
}

/** Creates one record with its contacts and addresses in a single transaction. */
export function createCrmRecordWithDetails(request: CreateCrmRecordWithDetailsRequest) {
  return fetchGeneralApiPost<CreateCrmRecordWithDetailsResult>("crm/records/create-with-details", request);
}

export function useCreateCrmRecordWithDetails() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCrmRecordWithDetails,
    // Refresh on every outcome, like move. A create may ack an onboarding instance, so reload those too.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: crmMastersQueryKey });
      void queryClient.invalidateQueries({ queryKey: customerOnboardingInstancesQueryKey });
    },
  });
}

/** Updates one record and its contacts and addresses in a single transaction. */
export function applyCrmRecordChanges(request: ApplyCrmRecordChangesRequest) {
  return fetchGeneralApiPut<ApplyCrmRecordChangesResult>("crm/records/apply-changes", request);
}

export function useApplyCrmRecordChanges() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: applyCrmRecordChanges,
    // Refresh on every outcome: a revision conflict also needs the latest revision loaded.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: crmMastersQueryKey });
    },
  });
}
